import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw, X, Mail, Loader2 } from 'lucide-react';
import AdminNav from '../components/AdminNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRY_OPTIONS = ['hotels', 'casinos', 'retail', 'airports', 'hospitals', 'stadiums'];
const TIER_OPTIONS = ['starter', 'small', 'medium', 'large', 'enterprise'];
const STATUS_OPTIONS = ['sent', 'stored', 'failed'];

const StatusBadge = ({ status }) => {
  const styles = {
    sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    stored: 'bg-slate-50 text-slate-600 border-slate-200',
    failed: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${styles[status] || styles.stored}`} data-testid={`status-badge-${status}`}>
      {status || 'stored'}
    </span>
  );
};

const DetailPanel = ({ item, onClose, onResend, resending }) => {
  if (!item) return null;

  const components = item.components || {};
  const compEntries = Object.entries(components).filter(([, v]) => v > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" data-testid="detail-overlay">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl relative" onClick={e => e.stopPropagation()} data-testid="detail-panel">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" aria-label="Close" data-testid="detail-close">
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-base font-bold text-slate-900 mb-1">{item.name}</h3>
        <p className="text-xs text-slate-500 mb-5">{item.company} &middot; {new Date(item.created_at).toLocaleString()}</p>

        <div className="space-y-5">
          {/* Contact */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Contact</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div><span className="text-slate-400 text-xs">Name</span><p className="text-slate-800 font-medium" data-testid="detail-name">{item.name}</p></div>
              <div><span className="text-slate-400 text-xs">Email</span><p className="text-slate-800 font-medium" data-testid="detail-email">{item.email}</p></div>
              <div><span className="text-slate-400 text-xs">Company</span><p className="text-slate-800 font-medium" data-testid="detail-company">{item.company}</p></div>
              <div><span className="text-slate-400 text-xs">Phone</span><p className="text-slate-800 font-medium" data-testid="detail-phone">{item.phone || '—'}</p></div>
            </div>
            {item.message && (
              <div className="mt-2"><span className="text-slate-400 text-xs">Message</span><p className="text-sm text-slate-700 mt-0.5 bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid="detail-message">{item.message}</p></div>
            )}
          </div>

          {/* Deployment */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Deployment</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div><span className="text-slate-400 text-xs">Industry</span><p className="text-slate-800 font-medium capitalize" data-testid="detail-industry">{item.industry}</p></div>
              <div><span className="text-slate-400 text-xs">Coverage</span><p className="text-slate-800 font-medium capitalize" data-testid="detail-coverage">{item.coverage_intensity}</p></div>
              <div><span className="text-slate-400 text-xs">Tag Range</span><p className="text-slate-800 font-medium font-mono" data-testid="detail-tag-range">{item.min_tags} – {item.max_tags}</p></div>
              <div><span className="text-slate-400 text-xs">Tier</span><p className="text-slate-800 font-medium capitalize" data-testid="detail-tier">{item.tier}</p></div>
            </div>
            {compEntries.length > 0 && (
              <div className="mt-3">
                <span className="text-slate-400 text-xs">Components</span>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="detail-components">
                  {compEntries.map(([k, v]) => (
                    <span key={k} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-md border border-slate-200">
                      {k.replace(/([A-Z])/g, ' $1').trim()}: {v}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Metadata</p>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div><span className="text-slate-400 text-xs">Language</span><p className="text-slate-800 font-medium uppercase" data-testid="detail-language">{item.language}</p></div>
              <div><span className="text-slate-400 text-xs">Source</span><p className="text-slate-800 font-medium" data-testid="detail-source">{item.source}</p></div>
              <div><span className="text-slate-400 text-xs">Email</span><StatusBadge status={item.email_status} /></div>
            </div>
          </div>
        </div>

        {/* Resend Email */}
        <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
          <button
            onClick={() => onResend(item.id)}
            disabled={resending}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 text-xs font-medium rounded-lg transition-colors"
            data-testid="detail-resend-btn"
          >
            {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Resend Email
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewRequestsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [resending, setResending] = useState(false);

  const [filters, setFilters] = useState({ industry: '', tier: '', email_status: '', date_from: '', date_to: '' });

  const fetchData = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 25 });
      if (filters.industry) params.set('industry', filters.industry);
      if (filters.tier) params.set('tier', filters.tier);
      if (filters.email_status) params.set('email_status', filters.email_status);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);

      const res = await fetch(`${API_URL}/api/contact/requests?${params}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/admin/login'); return; }
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, navigate, page]);

  useEffect(() => { fetchData(1); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = async (id) => {
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact/requests/${id}/resend-email`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedItem(prev => prev ? { ...prev, email_status: data.email_status } : prev);
        fetchData(page);
      }
    } catch { /* ignore */ }
    setResending(false);
  };

  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ industry: '', tier: '', email_status: '', date_from: '', date_to: '' });
  };

  const hasFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="review-requests-page">
      <AdminNav />

      {/* Sub-header with title + refresh */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-bold text-slate-900" data-testid="dashboard-title">Review Requests</h1>
            <span className="text-xs text-slate-400 font-mono" data-testid="dashboard-total">{total} total</span>
          </div>
          <button onClick={() => fetchData(page)} className="p-2 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Refresh" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="filters-bar">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Industry</label>
            <select value={filters.industry} onChange={e => handleFilter('industry', e.target.value)} className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid="filter-industry">
              <option value="">All</option>
              {INDUSTRY_OPTIONS.map(o => <option key={o} value={o} className="capitalize">{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Tier</label>
            <select value={filters.tier} onChange={e => handleFilter('tier', e.target.value)} className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid="filter-tier">
              <option value="">All</option>
              {TIER_OPTIONS.map(o => <option key={o} value={o} className="capitalize">{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Email Status</label>
            <select value={filters.email_status} onChange={e => handleFilter('email_status', e.target.value)} className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid="filter-email-status">
              <option value="">All</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o} className="capitalize">{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">From</label>
            <input type="date" value={filters.date_from} onChange={e => handleFilter('date_from', e.target.value)} className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">To</label>
            <input type="date" value={filters.date_to} onChange={e => handleFilter('date_to', e.target.value)} className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid="filter-date-to" />
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="px-2.5 py-1.5 text-xs text-slate-500 hover:text-red-600 transition-colors" data-testid="clear-filters">
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="requests-table-container">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="loading-state">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-slate-400" data-testid="empty-state">
              <p className="text-sm">No review requests found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="requests-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50">
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Date</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Name</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Company</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold hidden md:table-cell">Email</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold hidden lg:table-cell">Industry</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold hidden lg:table-cell">Tier</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold hidden sm:table-cell">Tag Range</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr
                      key={item.id || i}
                      onClick={() => setSelectedItem(item)}
                      className="border-b border-slate-100 hover:bg-slate-50/80 cursor-pointer transition-colors"
                      data-testid={`request-row-${i}`}
                    >
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(item.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-xs text-slate-800 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{item.company}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 hidden md:table-cell">{item.email}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 capitalize hidden lg:table-cell">{item.industry}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 capitalize hidden lg:table-cell">{item.tier}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 font-mono hidden sm:table-cell">{item.min_tags}–{item.max_tags}</td>
                      <td className="px-4 py-3"><StatusBadge status={item.email_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/30" data-testid="pagination">
              <p className="text-xs text-slate-400">Page {page} of {pages}</p>
              <div className="flex gap-1">
                <button
                  onClick={() => fetchData(page - 1)}
                  disabled={page <= 1}
                  className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 rounded transition-colors"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => fetchData(page + 1)}
                  disabled={page >= pages}
                  className="p-1.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 rounded transition-colors"
                  data-testid="pagination-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Panel */}
      {selectedItem && (
        <DetailPanel
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onResend={handleResend}
          resending={resending}
        />
      )}
    </div>
  );
};

export default ReviewRequestsPage;
